import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getEventDates } from './events';
import type { EventDateFacetDto, GetEventDatesV1Params } from '@/services/platform/api/model';

export interface UseEventDatesResult {
  dates: EventDateFacetDto[];
  isLoading: boolean;
  hasFetched: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches the distinct event dates (with per-day counts) for the calendar rail,
 * scoped to the same hub / event-type params as the listing so the two stay in sync.
 * `enabled: false` skips the fetch entirely (e.g. a category-scoped call with no category
 * selected — pointless when it would just duplicate the unfiltered one).
 */
export function useEventDates(params?: GetEventDatesV1Params, enabled = true): UseEventDatesResult {
  const locale = useLocaleStore(state => state.locale);
  const [dates, setDates] = useState<EventDateFacetDto[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasFetched, setHasFetched] = useState(false);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setDates([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getEventDates(params ?? {}, locale);
      setDates(result);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
    // paramsKey captures the params object contents; params itself is intentionally omitted.
  }, [paramsKey, enabled, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { dates, isLoading, hasFetched, refetch };
}
